/**
 * TypeScript mirror of the backend learning DTOs
 * (`eduviaos-backend/app/domains/learning/schemas.py`, Phase SC-3a).
 *
 * Study-centre-only surface. The BE hard-walls `/api/learning/*` to
 * `study_centre` tenants via `require_vertical`, so these types never load
 * for a school / coaching learner — keeping the vertical boundary intact on
 * the client too (nothing here leaks school/coaching vocabulary).
 */

export type LearningPurpose =
  | "exam_prep"
  | "concept_mastery"
  | "casual_reading"
  | "skill_building"
  | "school_support"
  | "other";

export type RoadmapSource = "ai_generated" | "self_chosen";
export type BookSource = "ai_pick" | "self_specified";
export type PathwayStatus = "active" | "paused" | "completed" | "archived";

export interface Pathway {
  id: string;
  tenant_id: string;
  learner_user_id: string;
  title: string;
  purpose: LearningPurpose;
  exam_name: string | null;
  subjects: string[];
  days_budget: number | null;
  daily_minutes: number | null;
  roadmap_source: RoadmapSource | null;
  book_source: BookSource | null;
  book_title: string | null;
  status: PathwayStatus;
  onboarding_complete: boolean;
  note: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface PathwayList {
  items: Pathway[];
  total: number;
  active_count: number;
}

export interface CreatePathwayInput {
  title: string;
  purpose: LearningPurpose;
  exam_name?: string | null;
  subjects?: string[];
  days_budget?: number | null;
  daily_minutes?: number | null;
  roadmap_source?: RoadmapSource | null;
  book_source?: BookSource | null;
  book_title?: string | null;
  note?: string | null;
}

export interface UpdatePathwayInput {
  title?: string;
  purpose?: LearningPurpose;
  exam_name?: string | null;
  subjects?: string[];
  days_budget?: number | null;
  daily_minutes?: number | null;
  roadmap_source?: RoadmapSource | null;
  book_source?: BookSource | null;
  book_title?: string | null;
  status?: PathwayStatus;
  onboarding_complete?: boolean;
  note?: string | null;
}

/** SC-9 — AI-generated multi-level study roadmap payload. */
export interface RoadmapGenerateResult {
  pathway_id: string;
  roadmap: Record<string, unknown>;
  provider: string | null;
  model: string | null;
}

/** SC-14a — self-paced course navigation derived from the AI roadmap. */
export type CourseLeafStatus = "locked" | "available" | "completed";
export type CourseCompletionSource = "manual" | "review" | "read_log" | "step_quiz";

export interface CourseLeaf {
  id: string;
  subject: string;
  topic: string;
  sub_topic: string;
  title: string;
  estimated_minutes: number | null;
  status: CourseLeafStatus;
  completion_source: CourseCompletionSource | null;
  read_log_count: number;
  min_questions_required: number;
  requires_step_quiz: boolean;
}

export interface CourseNavigation {
  pathway_id: string;
  has_roadmap: boolean;
  roadmap_mode: "ai_generated" | "self_chosen" | "none";
  pacing_notes: string | null;
  total_estimated_days: number | null;
  completion_percent: number;
  recommended_books: string[];
  leaves: CourseLeaf[];
}

/* ── Daily loop: diagnostic + review queue (Phase SC-3d) ──────────────── */

export type DiagnosticStatus = "in_progress" | "submitted" | "abandoned";

export interface DiagnosticOption {
  id: string;
  label: string;
}

/**
 * A question as the learner sees it *before* submitting. The BE ships two
 * separate DTOs rather than one with an optional answer key, so there is
 * no shape in which an unsubmitted question can carry `correct_option_id`
 * — mirrored here so the client can't accidentally read one either.
 */
export interface DiagnosticQuestion {
  id: string;
  subject: string;
  topic: string;
  prompt: string;
  options: DiagnosticOption[];
  bloom_level: string | null;
}

/** Post-submit shape: answer key + what the learner picked. */
export interface GradedQuestion extends DiagnosticQuestion {
  correct_option_id: string;
  selected_option_id: string | null;
  is_correct: boolean;
  explanation: string | null;
}

export interface DiagnosticSession {
  id: string;
  pathway_id: string;
  leaf_id: string | null;
  status: DiagnosticStatus;
  questions: DiagnosticQuestion[];
  created_at: string;
}

export interface TopicGap {
  subject: string;
  topic: string;
  asked: number;
  correct: number;
  accuracy: number;
}

export interface DiagnosticResult {
  id: string;
  pathway_id: string;
  leaf_id: string | null;
  status: DiagnosticStatus;
  score: number;
  step_passed: boolean | null;
  step_unlocked: boolean | null;
  asked: number;
  correct: number;
  /** Weakest-first — this IS the ranking the scheduler used. */
  topic_gaps: TopicGap[];
  questions: GradedQuestion[];
  cards_created: number;
  cards_updated: number;
  submitted_at: string;
}

/** `null` selection = skipped; graded wrong but kept distinct from a guess. */
export interface DiagnosticAnswer {
  question_id: string;
  selected_option_id: string | null;
}

export interface ReviewCard {
  id: string;
  pathway_id: string;
  pathway_title: string | null;
  subject: string;
  topic: string;
  due_at: string;
  interval_days: number;
  repetitions: number;
  lapses: number;
  easiness_factor: number;
  review_count: number;
  correct_count: number;
  mastery: number;
  status: string;
  last_reviewed_at: string | null;
  post_quiz_required?: boolean;
}

export interface DueCards {
  items: ReviewCard[];
  /** Due across ALL pathways — `items` is capped, this is the badge. */
  total_due: number;
}

/** SC-15 — mandatory follow-up quiz after each recall rating. */
export type PostReviewDifficulty = "easy" | "medium" | "hard";

export interface StartPostReviewQuizInput {
  question_count?: number;
  difficulty?: PostReviewDifficulty;
}

export interface PostReviewQuizSession {
  id: string;
  card_id: string;
  pathway_id: string;
  subject: string;
  topic: string;
  recall_quality: number;
  question_count: number;
  difficulty: PostReviewDifficulty;
  questions: DiagnosticQuestion[];
  status: DiagnosticStatus;
  created_at: string;
}

export interface PostReviewQuizResult {
  id: string;
  card_id: string;
  score: number;
  asked: number;
  correct: number;
  questions: GradedQuestion[];
  submitted_at: string;
  self_reported_quality?: number | null;
  effective_quality?: number | null;
  grade_reconciled?: boolean;
  reconciliation_message?: string | null;
}

/** SC-16 — resolved tenant + student caps and content language. */
export type LearningContentLanguage = "en" | "hi";

export interface ResolvedLearningPreferences {
  max_questions_per_quiz: number;
  default_diagnostic_question_count: number;
  default_practice_question_count: number;
  default_post_review_question_count: number;
  default_skill_assessment_question_count: number;
  content_language: LearningContentLanguage;
  ziggy_variant: "pink" | "blue" | null;
}

export interface StartDiagnosticInput {
  question_count?: number;
  leaf_id?: string;
}

export interface TrackRecordDetail extends TrackRecordEntry {
  detail: TrackRecordSessionDetail | null;
}

export interface TrackRecordSessionDetail {
  score?: number;
  questions?: GradedQuestion[];
  recall_quality?: number;
  self_reported_quality?: number;
  effective_quality?: number;
  grade_reconciled?: boolean;
  gap_analysis?: unknown;
  weakness_report?: WeaknessReport;
  quality?: number;
  quiz_score?: number;
  passed?: boolean;
  subject?: string;
  topic?: string;
  minutes_spent?: number;
}

export interface CoachSummary {
  due_now: number;
  reviewed_today: number;
  reads_logged_today: number;
  unread_notifications: number;
  cards_total: number;
  next_due_at: string | null;
  /** Active, onboarded pathways with no submitted diagnostic yet. */
  pathways_needing_diagnostic: string[];
  /** Consecutive days with study activity (reads, reviews, or tests). */
  study_streak_days: number;
  /** Estimated focus minutes today (logged reading + review time). */
  focus_minutes_today: number;
}

export interface CreateReadLogInput {
  subject: string;
  topic: string;
  minutes_spent?: number;
  note?: string;
}

export interface ReadLogEntry {
  id: string;
  pathway_id: string;
  subject: string;
  topic: string;
  minutes_spent: number | null;
  note: string | null;
  logged_at: string;
  card_created: boolean;
  card_updated: boolean;
}

export interface ReadLogList {
  items: ReadLogEntry[];
  total: number;
}

export interface WeaknessFocusArea {
  subject: string;
  topic: string;
  severity: "high" | "medium" | "low";
  advice: string;
}

export interface WeaknessReport {
  headline: string;
  summary: string;
  focus_areas: WeaknessFocusArea[];
}

export interface PracticeTestSession {
  id: string;
  pathway_id: string;
  status: DiagnosticStatus;
  requested_topics: string[];
  question_count: number;
  questions: DiagnosticQuestion[];
  created_at: string;
}

export interface CreatePracticeTestInput {
  topics: string[];
  question_count?: number;
}

export interface PracticeTestResult extends DiagnosticResult {
  weakness_report: WeaknessReport;
}

/* ── Track record + progress reports (Phase SC-12) ───────────────────── */

export type ActivityType =
  | "read_log"
  | "card_review"
  | "diagnostic_submitted"
  | "practice_submitted";

export interface TrackRecordEntry {
  id: string;
  pathway_id: string;
  activity_type: ActivityType;
  title: string;
  summary: string | null;
  payload: Record<string, unknown>;
  occurred_at: string;
}

export interface TrackRecordList {
  items: TrackRecordEntry[];
  total: number;
}

export interface GenerateProgressReportInput {
  period_days?: number;
}

export interface ProgressReport {
  id: string;
  pathway_id: string;
  period_days: number;
  stats_snapshot: Record<string, unknown>;
  narrative: string;
  focus_areas: string[];
  encouragement: string | null;
  generated_at: string;
}

export interface ProgressReportList {
  items: ProgressReport[];
  total: number;
}

export interface LearnerNotification {
  id: string;
  notification_type: string;
  title: string;
  body: string;
  link_path: string | null;
  related_id: string | null;
  read_at: string | null;
  created_at: string;
}

export interface LearnerNotificationList {
  items: LearnerNotification[];
  total: number;
  unread_count: number;
}

/** SC-14b — skill assessment + certificates (FEATURE-LIST 4.34). */
export interface CreateSkillAssessmentInput {
  subject: string;
  topic: string;
  question_count?: number;
}

export interface SkillAssessmentSession {
  id: string;
  pathway_id: string;
  subject: string;
  topic: string;
  status: DiagnosticStatus;
  question_count: number;
  questions: DiagnosticQuestion[];
  created_at: string;
}

export interface SkillCertificate {
  id: string;
  pathway_id: string;
  pathway_title: string;
  subject: string;
  topic: string;
  score: number;
  verify_code: string;
  streak_days: number;
  consistency_pct: number;
  topic_reviews: number;
  issued_at: string;
}

export interface SkillCertificateList {
  items: SkillCertificate[];
  total: number;
}

/** Anonymous GET /api/public/skill-certificates/{verify_code}. */
export interface SkillCertificateVerify {
  valid: boolean;
  tenant_slug: string | null;
  pathway_title: string | null;
  subject: string | null;
  topic: string | null;
  score: number | null;
  streak_days: number | null;
  consistency_pct: number | null;
  topic_reviews: number | null;
  issued_at: string | null;
}

export interface SkillAssessmentResult extends DiagnosticResult {
  subject: string;
  topic: string;
  passed: boolean;
  certificate: SkillCertificate | null;
}

/** SC-14b — peer study groups (FEATURE-LIST 4.38). */
export interface CreateStudyGroupInput {
  name: string;
  description?: string | null;
  pathway_id?: string | null;
  subject?: string | null;
  topic?: string | null;
  max_members?: number;
}

export interface StudyGroup {
  id: string;
  name: string;
  description: string | null;
  pathway_id: string | null;
  subject: string | null;
  topic: string | null;
  created_by: string;
  member_count: number;
  max_members: number;
  is_member: boolean;
  is_admin?: boolean;
  is_moderator?: boolean;
  chat_username?: string | null;
  invite_code: string;
  invite_link_mode: "permanent" | "single_use";
  archived_at?: string | null;
  created_at: string;
}

export interface StudyGroupList {
  items: StudyGroup[];
  total: number;
  archived_items?: StudyGroup[];
}

export interface StudyGroupMessageReadByEntry {
  user_id: string;
  display_name: string;
  read_at: string;
}

export type StudyGroupMessageDeliveryStatus = "processing" | "delivered" | "failed";

export interface StudyGroupMessage {
  id: string;
  group_id: string;
  author_user_id: string;
  author_name: string;
  kind: "text" | "image";
  body: string;
  image_url: string | null;
  mention_user_ids: string[];
  client_message_id?: string | null;
  delivery_status?: StudyGroupMessageDeliveryStatus;
  failure_reason?: string | null;
  read_by?: StudyGroupMessageReadByEntry[];
  link_previews?: { url: string; host?: string | null }[] | null;
  is_deleted?: boolean;
  deleted_by_name?: string | null;
  deleted_at?: string | null;
  created_at: string;
}

export interface StudyGroupChatProfile {
  group_id: string;
  user_id: string;
  chat_username: string | null;
}

export type StudyGroupRealtimeEvent =
  | { type: "message.new"; group_id: string; message: StudyGroupMessage }
  | { type: "message.status"; group_id: string; message: StudyGroupMessage }
  | { type: "message.updated"; group_id: string; message: StudyGroupMessage }
  | { type: "message.deleted"; group_id: string; message_id: string }
  | { type: "read.receipt"; group_id: string; message_id: string; message: StudyGroupMessage }
  /** ``local``: the server can only fan out events from its own worker. */
  | { type: "realtime.mode"; mode: "redis" | "local" }
  | {
      type: "typing.start" | "typing.stop" | "presence.join" | "presence.leave";
      group_id: string;
      user_id: string;
      display_name?: string;
    };

/** Backend sentinel for @all mentions (Phase SC-14d). */
export const STUDY_GROUP_MENTION_ALL = "__all__";

export type LearnHubAction = "quiz" | "practice" | "reading" | "progress" | "roadmap";

export interface StudyGroupMessageList {
  items: StudyGroupMessage[];
  total: number;
}

export interface StudyGroupMember {
  user_id: string;
  display_name: string;
  chat_username?: string | null;
  avatar_url: string | null;
  is_admin: boolean;
  is_moderator?: boolean;
}

export interface StudyGroupMemberPlanSummary {
  id: string;
  title: string;
  subject: string | null;
}

export interface StudyGroupMessageReceiptEntry {
  user_id: string;
  display_name: string;
  status: "seen" | "delivered" | "pending" | "not_delivered";
  read_at: string | null;
}

export interface StudyGroupMessageReceiptList {
  message_id: string;
  items: StudyGroupMessageReceiptEntry[];
  total: number;
}

export interface StudyGroupMemberList {
  items: StudyGroupMember[];
  total: number;
}

export interface StudyGroupMemberStats {
  user_id: string;
  display_name: string;
  chat_username?: string | null;
  avatar_url: string | null;
  due_now: number;
  reviewed_today: number;
  streak_days: number;
  active_plans: number;
  cards_total: number;
  plan_summaries?: StudyGroupMemberPlanSummary[];
}

export interface SignedUploadResponse {
  put_url: string;
  public_url: string;
  object_path: string;
  expires_in_seconds: number;
  required_headers: Record<string, string>;
}
